import React, { useState } from 'react';
import { Link, NavLink } from '@remix-run/react';
import { Menu, Search, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { routes } from '~/lib/routes';
import { cn } from '~/lib/utils';

const NAVIGATION_LINKS = [
  { name: 'Home', to: routes.home() },
  { name: 'Game', to: routes.about() },
  { name: 'Database', to: routes.database.index() },
  { name: 'Devlog', to: routes.blog.index() },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleMobile = () => setMobileOpen((prev) => !prev);
  const closeMobile = () => setMobileOpen(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur md:sticky md:top-0">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <Link to={routes.home()} className="inline-flex items-center gap-2">
            <img
              src="/images/lumora-logo.png"
              alt="Lumora Logo"
              className="h-10 w-10 rounded-full shadow-lg"
            />
            <span className="hidden text-lg font-semibold text-foreground sm:inline">Lumora</span>
          </Link>
        </div>

        <nav className="hidden items-center gap-6 text-sm font-medium md:flex">
          {NAVIGATION_LINKS.map((link) => (
            <NavLink
              key={link.name}
              to={link.to}
              prefetch="intent"
              className={({ isActive }) =>
                cn(
                  'transition hover:text-foreground',
                  isActive ? 'text-foreground' : 'text-muted-foreground'
                )
              }
            >
              {link.name}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to={routes.search()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/60 text-muted-foreground transition hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label="Search"
          >
            <Search className="h-4 w-4" aria-hidden />
          </Link>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={toggleMobile}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileOpen}
          >
            <Menu className="h-5 w-5" aria-hidden />
          </Button>
        </div>
      </div>

      <div
        className={cn(
          'fixed inset-0 z-30 flex h-screen min-h-screen flex-col bg-background transition-all duration-300 md:hidden',
          mobileOpen ? 'pointer-events-auto translate-x-0' : 'pointer-events-none translate-x-full'
        )}
        aria-hidden={!mobileOpen}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <span className="text-lg font-semibold text-foreground">Menu</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={closeMobile}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" aria-hidden />
          </Button>
        </div>
        <nav className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 py-6">
          {NAVIGATION_LINKS.map((link) => (
            <NavLink
              key={link.name}
              to={link.to}
              prefetch="intent"
              onClick={closeMobile}
              className={({ isActive }) =>
                cn(
                  'rounded-md px-4 py-3 text-base font-medium transition',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              {link.name}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border px-6 py-4">
          <Link
            to={routes.search()}
            onClick={closeMobile}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-muted px-4 py-3 text-base font-medium text-foreground transition hover:bg-muted/80"
          >
            <Search className="h-5 w-5" aria-hidden />
            Search
          </Link>
        </div>
      </div>
    </header>
  );
}
